import { Component, type ErrorInfo, type ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error("React Error Boundary Caught an Error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  private isLocalOrIP(): boolean {
    const host = window.location.hostname;
    return (
      host === "localhost" ||
      host === "127.0.0.1" ||
      host === "::1" ||
      /^\d{1,3}(\.\d{1,3}){3}$/.test(host)
    );
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.isLocalOrIP()) {
        return (
          <div className="p-6 bg-red-100 text-red-800 rounded-xl shadow-md">
            <h1 className="text-xl font-bold mb-2">Something went wrong.</h1>
            <pre className="whitespace-pre-wrap text-sm overflow-auto">
              {this.state.error?.toString()}
              {"\n\n"}
              {this.state.errorInfo?.componentStack}
            </pre>
          </div>
        );
      }
      return (
        <div className="p-6 bg-gray-100 text-gray-800 rounded-xl shadow-md">
          <h1 className="text-xl font-bold">Oops! Something went wrong.</h1>
          <p>Try refreshing the page.</p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
